import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../utils/api';
import { PRIORITY_COLORS, STATUS_COLORS } from '../utils/config';

export default function TaskList({ user }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', priority: '', keyword: '' });
  const navigate = useNavigate();

  const isManager = ['admin','manager','general_manager'].includes(user.role);
  const reviewRoles = ['admin','general_manager','manager','steward_chief','steward','finance'];

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.priority) params.priority = filter.priority;
      if (filter.keyword) params.keyword = filter.keyword;
      const reviewReq = reviewRoles.includes(user.role)
        ? API.get('/property-archive/review-tasks', { params }).catch(() => ({data:[]}))
        : Promise.resolve({data:[]});
      const [res, reviewRes] = await Promise.all([API.get('/tasks', { params }), reviewReq]);
      const merged = [...reviewRes.data, ...res.data].sort((a,b) => new Date(b.created_at) - new Date(a.created_at));
      setTasks(merged);
    } catch (err) {
      alert('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, [filter.status, filter.priority]);

  const getLocation = (t) => {
    if (t.task_type === 'property_review') return t.review_room_no || t.public_area || '';
    if (t.location_type === '住宅') return `${t.building}${t.unit}${t.floor}层${t.room}`;
    return t.public_area || '';
  };

  return (
    <div style={styles.container}>
      {/* 顶部标题栏 */}
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>代办事项</h1>
        <div style={styles.headerBtns}>
          {/* 投屏按钮 */}
          <button style={styles.screenBtn}
            onClick={() => window.open('/dashboard', '_blank')}>
            📺
          </button>
          {['admin','manager','general_manager','steward_chief','steward','engineer','cleaner','security','owner'].includes(user.role) && (
            <button style={styles.addBtn} onClick={() => navigate('/add')}>+ 新增</button>
          )}
        </div>
      </div>

      {/* 筛选栏 */}
      <div style={styles.filterBar}>
        <select style={styles.select} value={filter.status} onChange={e => setFilter({...filter, status: e.target.value})}>
          <option value="">全部状态</option>
          <option value="待处理">待处理</option>
          <option value="处理中">处理中</option>
          <option value="已完成">已完成</option>
        </select>
        <select style={styles.select} value={filter.priority} onChange={e => setFilter({...filter, priority: e.target.value})}>
          <option value="">全部优先级</option>
          <option value="紧急">紧急</option>
          <option value="高">高</option>
          <option value="中">中</option>
          <option value="低">低</option>
        </select>
      </div>

      {/* 搜索栏 */}
      <div style={styles.searchBar}>
        <input style={styles.searchInput} placeholder="搜索事项..."
          value={filter.keyword}
          onChange={e => setFilter({...filter, keyword: e.target.value})}
          onKeyDown={e => e.key === 'Enter' && fetchTasks()} />
        <button style={styles.searchBtn} onClick={fetchTasks}>搜索</button>
      </div>

      {/* 事项列表 */}
      {loading ? (
        <div style={styles.loading}>加载中...</div>
      ) : tasks.length === 0 ? (
        <div style={styles.empty}>暂无事项</div>
      ) : (
        <div style={styles.list}>
          {tasks.map(task => (
            <div key={task.id} style={{...styles.card, borderLeftColor: PRIORITY_COLORS[task.priority]}}
              onClick={() => navigate(task.task_type === 'property_review' ? `/review-task/${task.review_request_id}` : `/task/${task.id}`)}>
              <div style={styles.cardTop}>
                <span style={{...styles.priorityBadge, background: PRIORITY_COLORS[task.priority]}}>
                  {task.priority}
                </span>
                <span style={{...styles.statusBadge, background: STATUS_COLORS[task.status]}}>
                  {task.status}
                </span>
                <span style={styles.dept}>{task.task_type === 'property_review' ? '📝 审核任务' : (task.department || (task.assign_user_name ? `👤${task.assign_user_name}` : '未分配'))}</span>
              </div>
              <div style={styles.cardTitle}>{task.title}</div>
              <div style={styles.cardInfo}>
                <span>📍 {getLocation(task)}</span>
                <span>来源：{task.source}</span>
              </div>
              <div style={styles.cardFooter}>
                <span>{task.creator_name} · {new Date(task.created_at).toLocaleDateString('zh-CN')}</span>
                {task.images?.length > 0 && <span>📷 {task.images.length}张</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', background: '#f5f7fa', paddingBottom: '20px' },
  header: {
    background: 'linear-gradient(90deg, #1a4a8a, #2d7dd2)',
    padding: '16px', display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', position: 'sticky', top: 0, zIndex: 100
  },
  headerTitle: { color: '#fff', margin: 0, fontSize: '18px', fontWeight: 700 },
  headerBtns: { display: 'flex', gap: '8px', alignItems: 'center' },
  screenBtn: {
    background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '8px', padding: '6px 10px', fontSize: '18px', cursor: 'pointer'
  },
  addBtn: {
    background: '#fff', color: '#1a4a8a', border: 'none',
    borderRadius: '20px', padding: '8px 18px', fontSize: '15px', fontWeight: 600, cursor: 'pointer'
  },
  filterBar: { display: 'flex', gap: '8px', padding: '12px 16px 0' },
  select: { flex: 1, padding: '8px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', background: '#fff' },
  searchBar: { display: 'flex', gap: '8px', padding: '8px 16px' },
  searchInput: { flex: 1, padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: '8px', fontSize: '14px' },
  searchBtn: { padding: '8px 16px', background: '#1a4a8a', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  loading: { textAlign: 'center', padding: '40px', color: '#888' },
  empty: { textAlign: 'center', padding: '60px', color: '#bbb', fontSize: '16px' },
  list: { padding: '0 16px' },
  card: {
    background: '#fff', borderRadius: '12px', padding: '14px',
    marginBottom: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    cursor: 'pointer', borderLeft: '4px solid #1a4a8a'
  },
  cardTop: { display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' },
  priorityBadge: { color: '#fff', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 600 },
  statusBadge: { color: '#fff', borderRadius: '4px', padding: '2px 8px', fontSize: '12px' },
  dept: { color: '#666', fontSize: '12px', padding: '2px 8px', background: '#f0f0f0', borderRadius: '4px' },
  cardTitle: { fontSize: '15px', fontWeight: 600, color: '#222', marginBottom: '6px' },
  cardInfo: { display: 'flex', gap: '12px', fontSize: '13px', color: '#666', marginBottom: '6px' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#999' }
};
